import * as React from "react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./alert-dialog"

// Contexte exposant une fonction `confirm()` qui remplace window.confirm()
// par une fenêtre de confirmation intégrée à l'application (au lieu de la
// popup native du navigateur).
const ConfirmDialogContext = React.createContext(null)

export function ConfirmDialogProvider({ children }) {
  const [request, setRequest] = React.useState(null)
  const resolverRef = React.useRef(null)

  const confirm = React.useCallback((options) => {
    const normalized = typeof options === "string" ? { description: options } : (options || {})
    return new Promise((resolve) => {
      resolverRef.current = resolve
      setRequest({
        title: normalized.title || "Confirmation",
        description: normalized.description || "",
        confirmText: normalized.confirmText || "Confirmer",
        cancelText: normalized.cancelText || "Annuler",
        destructive: normalized.destructive !== false,
      })
    })
  }, [])

  const settle = React.useCallback((value) => {
    if (resolverRef.current) {
      resolverRef.current(value)
      resolverRef.current = null
    }
    setRequest(null)
  }, [])

  return (
    <ConfirmDialogContext.Provider value={confirm}>
      {children}
      <AlertDialog open={!!request} onOpenChange={(open) => { if (!open) settle(false) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{request?.title}</AlertDialogTitle>
            {request?.description && (
              <AlertDialogDescription className="whitespace-pre-line">
                {request.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => settle(false)}>
              {request?.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => settle(true)}
              className={request?.destructive ? "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90" : undefined}
            >
              {request?.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmDialogContext.Provider>
  )
}

// Hook à utiliser à la place de window.confirm(message).
// Exemple : const confirm = useConfirm(); if (!(await confirm("Supprimer ?"))) return;
export function useConfirm() {
  const confirm = React.useContext(ConfirmDialogContext)
  if (!confirm) {
    throw new Error("useConfirm doit être utilisé à l'intérieur d'un ConfirmDialogProvider")
  }
  return confirm
}
